import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiEdit2, FiTrash2 } from 'react-icons/fi';
import { ConfirmationDialog, PageHeader, useToast } from 'components/admin';
import { Button, Input, Select, Table } from 'components/common';
import { deleteProductSolutionCategory, getAdminProductSolutionCategories } from 'apis/adminProductSolutionCategories';
import { getAdminProductSolutionGroups } from 'apis/adminProductSolutionGroups';
import { useActiveLanguagesQuery, useAdminDeleteMutation, useAdminListQuery } from 'hooks';
import '../../../styles/admin-shared.scss';

const ProductSolutionCategoriesList = () => {
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize] = useState(10);
    const [keyword, setKeyword] = useState('');
    const [isActiveFilter, setIsActiveFilter] = useState('');
    const [languageCodeFilter, setLanguageCodeFilter] = useState('vi');
    const [groupFilter, setGroupFilter] = useState('');
    const [deleteId, setDeleteId] = useState(null);
    const toast = useToast();
    const navigate = useNavigate();

    const { data: languages = [] } = useActiveLanguagesQuery();
    const { data: groupsData = { items: [] } } = useAdminListQuery('product-solution-groups-options', { pageSize: 1000, languageCode: languageCodeFilter || 'vi' }, getAdminProductSolutionGroups);
    const groups = groupsData.items;
    const queryParams = useMemo(() => ({
        pageNumber: currentPage,
        pageSize,
        keyword: keyword || undefined,
        isActive: isActiveFilter === '' ? undefined : isActiveFilter === 'true',
        languageCode: languageCodeFilter || undefined,
        productSolutionGroupId: groupFilter || undefined,
    }), [currentPage, pageSize, keyword, isActiveFilter, languageCodeFilter, groupFilter]);
    const { data = { items: [], total: 0, currentPage, pageSize }, isFetching, error } = useAdminListQuery('product-solution-categories', queryParams, getAdminProductSolutionCategories);
    const deleteMutation = useAdminDeleteMutation('product-solution-categories', deleteProductSolutionCategory);

    useEffect(() => { setCurrentPage(1); }, [keyword, isActiveFilter, languageCodeFilter, groupFilter]);
    useEffect(() => { if (error) toast.error(error.message || 'T?i d? li?u th?t b?i'); }, [error, toast]);

    const handleDelete = async () => {
        try {
            const res = await deleteMutation.mutateAsync(deleteId);
            if (res?.success) {
                toast.success('Xóa thành công');
            } else {
                toast.error(res?.message || 'Xóa thất bại');
            }
        } catch {
            toast.error('Xóa thất bại');
        }
        setDeleteId(null);
    };

    const groupMap = useMemo(() => {
        const map = {};
        const list = Array.isArray(groupsData?.items) ? groupsData.items : (Array.isArray(groupsData) ? groupsData : []);
        list.forEach((g) => {
            const id = g.id || g.Id;
            if (id) {
                map[id] = g.name || g.Name || g.code;
                map[String(id)] = g.name || g.Name || g.code;
                map[String(id).toLowerCase()] = g.name || g.Name || g.code;
            }
        });
        return map;
    }, [groupsData]);

    const columns = [
        { field: 'id', header: 'ID' },
        {
            header: 'Hình ảnh',
            render: (row) => row.image ? <img src={row.image} alt="img" style={{ width: 40, height: 40, objectFit: 'cover', borderRadius: 4 }} /> : <span style={{ color: '#ccc', fontSize: 12 }}>Chưa có</span>,
        },
        { header: 'Tên danh mục', render: (row) => <strong style={{ display: 'inline-block' }}>{row.name || '—'}</strong> },
        {
            header: 'Nhóm',
            render: (row) => {
                const groupId = row.productSolutionGroupId || row.ProductSolutionGroupId;
                const gName = row.groupName || (groupId ? groupMap[groupId] || groupMap[String(groupId)] : null);
                return <span className="badge badge--info" style={{ whiteSpace: 'nowrap' }}>{gName || '—'}</span>;
            },
        },
        {
            header: 'Slug',
            render: (row) => <span style={{ color: '#64748b', fontSize: 13, wordBreak: 'break-word', maxWidth: 220, display: 'inline-block' }}>{row.slug || '—'}</span>,
        },
        { field: 'sortOrder', header: 'Thứ tự' },
        {
            header: 'Trạng thái',
            render: (row) => (
                <span className={`status-badge ${row.isActive ? 'active' : 'inactive'}`}>
                    {row.isActive ? 'Hoạt động' : 'Không hoạt động'}
                </span>
            ),
        },
        {
            header: 'Thao tác',
            render: (row) => (
                <div className="action-buttons">
                    <button className="icon-btn icon-btn--edit" onClick={() => navigate(`/admin/product-solution-categories/edit/${row.id}`)} title="Sửa"><FiEdit2 size={15} /></button>
                    <button className="icon-btn icon-btn--delete" onClick={() => setDeleteId(row.id)} title="Xóa"><FiTrash2 size={15} /></button>
                </div>
            ),
        },
    ];

    return (
        <div className="admin-page">
            <PageHeader
                title="Danh mục Sản phẩm / Giải pháp"
                subtitle="Quản lý các danh mục sản phẩm và giải pháp"
                action={<Button onClick={() => navigate('/admin/product-solution-categories/create')}>+ Thêm Danh mục</Button>}
            />
            <div className="filters-bar">
                <div className="filter-item">
                    <Input placeholder="Tìm kiếm theo tên..." value={keyword} onChange={e => setKeyword(e.target.value)} />
                </div>
                <div className="filter-item">
                    <Select value={groupFilter} onChange={e => setGroupFilter(e.target.value)}
                        options={[
                            { value: '', label: 'Tất cả nhóm' },
                            ...groups.map(g => ({ value: g.id, label: g.name || `Nhóm #${g.id}` })),
                        ]}
                    />
                </div>
                <div className="filter-item">
                    <Select value={isActiveFilter} onChange={e => setIsActiveFilter(e.target.value)}
                        options={[
                            { value: '', label: 'Tất cả trạng thái' },
                            { value: 'true', label: 'Hoạt động' },
                            { value: 'false', label: 'Không hoạt động' },
                        ]}
                    />
                </div>
                <div className="filter-item">
                    <Select value={languageCodeFilter} onChange={e => setLanguageCodeFilter(e.target.value)}
                        options={languages.map(l => ({ value: l.code, label: l.name }))}
                    />
                </div>
            </div>
            <Table
                columns={columns}
                data={data.items}
                loading={isFetching}
                pagination={{
                    currentPage: data.currentPage,
                    totalPages: Math.ceil(data.total / data.pageSize) || 1,
                    onPageChange: setCurrentPage,
                }}
            />
            <ConfirmationDialog
                open={!!deleteId}
                onClose={() => setDeleteId(null)}
                onConfirm={handleDelete}
                title="Xóa Danh mục Sản phẩm / Giải pháp"
                message="Bạn có chắc chắn muốn xóa danh mục này không?"
            />
        </div>
    );
};

export default ProductSolutionCategoriesList;
